import { useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useParams, useLocation, Link } from "wouter";
import { ArrowRight, PlugsConnected } from "@phosphor-icons/react";
import type { IntegrationKind } from "scantling-common";
import { useApi } from "./components.js";
import { HelmLayout } from "./layouts.js";
import {
  getIntegrationProvidersByKind,
  type IntegrationProviderComponent,
} from "./integration_registry.js";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";

interface KindCopy {
  emptyTitle: string;
  emptyDescription: string;
  pageTitle: string;
  pageDescription: string;
  manageLabel: string;
  managePath: string;
}

const COPY: Record<IntegrationKind, KindCopy> = {
  source: {
    emptyTitle: "No sources available",
    emptyDescription: "No source integrations have been registered.",
    pageTitle: "Add a source",
    pageDescription:
      "Connect a new source to bring inbound content into your inbox.",
    manageLabel: "Manage sources",
    managePath: "inbox?tab=sources",
  },
  channel: {
    emptyTitle: "No channels available",
    emptyDescription: "No channel integrations have been registered.",
    pageTitle: "Add a channel",
    pageDescription: "Connect a new channel to publish posts from your outbox.",
    manageLabel: "Manage channels",
    managePath: "outbox?tab=channels",
  },
};

interface ProviderTileProps {
  provider: IntegrationProviderComponent;
  onPick: (type: string) => void;
  disabled: boolean;
}

const ProviderTile = ({ provider, onPick, disabled }: ProviderTileProps) => {
  const Icon = provider.icon;
  return (
    <button
      type="button"
      onClick={() => onPick(provider.type)}
      disabled={disabled}
      className="text-left transition hover:ring-2 hover:ring-foreground/20 disabled:opacity-50"
    >
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
            <Icon />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">{provider.label}</CardTitle>
            <CardDescription>
              {provider.kind === "channel" ? "Output channel" : "Input source"}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>
    </button>
  );
};

const IntegrationsContent = ({ kind }: { kind: IntegrationKind }) => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { getToken } = useAuth();
  const pool = useApi();
  const [, setLocation] = useLocation();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const providers = getIntegrationProvidersByKind(kind);
  const copy = COPY[kind];

  const handlePick = useCallback(
    async (type: string) => {
      setCreating(true);
      setError(null);
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) throw new Error("Not authenticated");
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) throw new Error("No org available");
        const result = await orgApi.executeAction({
          verb: "createIntegration",
          params: { type },
        });
        if (!result.success) {
          throw new Error(result.error ?? "Action failed");
        }
        const slug = result.data?.slug as string | undefined;
        if (!slug) throw new Error("No slug returned");
        setLocation(`/o/${orgSlug}/n/${slug}`);
      } catch (err) {
        setError(`${err}`);
        setCreating(false);
      } finally {
        pool.release();
      }
    },
    [getToken, pool, orgSlug, setLocation],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-row items-start gap-3">
        <div className="flex-1">
          <h1 className="text-xl font-semibold">{copy.pageTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {copy.pageDescription}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link href={`/o/${orgSlug}/${copy.managePath}`}>
            {copy.manageLabel}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {providers.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>
            <PlugsConnected />
          </EmptyStateIcon>
          <EmptyStateTitle>{copy.emptyTitle}</EmptyStateTitle>
          <EmptyStateDescription>{copy.emptyDescription}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {providers.map(provider => (
            <ProviderTile
              key={provider.type}
              provider={provider}
              onPick={handlePick}
              disabled={creating}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const SourcesPage = () => (
  <HelmLayout>
    <IntegrationsContent kind="source" />
  </HelmLayout>
);

export const ChannelsPage = () => (
  <HelmLayout>
    <IntegrationsContent kind="channel" />
  </HelmLayout>
);
